import { useEffect, useRef, useState } from 'react'

import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import { indexes } from '@mab-common/utils'

import classNames from 'classnames'

import CategoryNavigation from '~/components/blocks/CategoryNavigation/CategoryNavigation'
import { useInPageNavContext } from '~/contexts/InPageNavContext'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import Container, { gutterTypes } from '../Container/Container'
import { Icon } from '../Icon/Icon'
import Link from '../Link/Link'

import styles from './MagazineDropdown.module.css'
import RecordTypes = indexes.RecordTypes
import type { ArticlesPageData } from '~/utils/articles/getArticlesPageProps'

const Catalogue = dynamic(
  () => import('~/components/blocks/Catalogue/Catalogue')
)

const SlideOutPanel = dynamic(() => import('../SlideOutPanel/SlideOutPanel'))

export interface MagazineDropdownProps {
  featuredCategories: ArticlesPageData['featuredCategories']
  articleArtists?: any
  testID?: string
  showLogo?: boolean
  selectedSlug?: string
  selectedArtistName?: string
  noBorder?: boolean
  testAttr?: string
}

export default function MagazineDropdown({
  featuredCategories,
  articleArtists,
  testID,
  showLogo = false,
  selectedSlug,
  selectedArtistName,
  noBorder = false,
  testAttr = 'magazine-dropdown',
}: MagazineDropdownProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpLarge})`)

  const [showArtistNav, setArtistNav] = useState<boolean>(false)

  const [isElementTouchingTop, setIsElementTouchingTop] =
    useState<boolean>(false)

  const { mainHeaderVisible } = useInPageNavContext()

  const router = useRouter()
  const path = router.asPath

  const filtersRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      const element = filtersRef.current
      if (element) {
        const elementRect = element.getBoundingClientRect()
        const topValue = mainHeaderVisible ? 54 : 0
        const isTouchingTop = elementRect.top <= topValue
        setTimeout(() => {
          setIsElementTouchingTop(isTouchingTop)
        }, 10)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleArtistClick = () => {
    setArtistNav(true)
  }

  return (
    <>
      <div
        className={classNames(styles.base, {
          [styles.stuck]: isElementTouchingTop,
          [styles.mergeNav]: mainHeaderVisible,
          [styles.noBorder]: noBorder,
        })}
        data-testid={testID}
        ref={filtersRef}
        data-test={testAttr}
      >
        {isElementTouchingTop}
        <Container gutters={gutterTypes.always} cssClass={styles.container}>
          <div className={classNames(styles.inner)}>
            {!isMobile && showLogo && (
              <Link className={styles.logo} href="/articles">
                <Icon type="EditionLogo" />
              </Link>
            )}
            <div className={classNames(styles.scroller)}>
              <div
                className={classNames(
                  styles.articleFilters,
                  path &&
                    !path.includes('category') &&
                    !path.includes('artist-') &&
                    styles.articleFiltersNoCategory
                )}
              >
                <CategoryNavigation
                  featuredCategories={featuredCategories}
                  handleArtistClick={handleArtistClick}
                  selectedSlug={selectedSlug}
                  selectedArtistName={selectedArtistName}
                />
              </div>
            </div>
          </div>
        </Container>
      </div>

      <SlideOutPanel
        isShowing={showArtistNav}
        closeHandler={() => setArtistNav(false)}
        title="Artist Filter"
      >
        <div>
          <Catalogue
            items={articleArtists}
            orderBySurname={true}
            recordType={RecordTypes.ARTICLES}
            closeParentMenu={() => setArtistNav(false)}
          />
        </div>
      </SlideOutPanel>
    </>
  )
}
